import React, { useState, useMemo } from 'react'
import { Card, Badge, EmptyState } from './ui'
import { TELESALES_AGENTS } from '../lib/constants'
import { ChevronLeft, ChevronRight, Clock } from 'lucide-react'
import { todayWIB } from './MyWorkToday'
import { dayAgenda, agendaCounts } from '../lib/calendar'

function addDays(dateStr, n) {
  const d = new Date(dateStr + 'T00:00:00')
  d.setDate(d.getDate() + n)
  return d.toLocaleDateString('en-CA')
}
const prettyDate = (d) => new Date(d + 'T00:00:00').toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })

const KIND_ACCENT = { reminder: '#185FA5', task: '#854F0B', todo: '#0F6E56' }
const KIND_VARIANT = { reminder: 'info', task: 'warning', todo: 'success' }

// Combined day agenda: lead follow-ups + engine tasks + personal to-dos for one date.
export default function DayCalendar({
  tasks = [], reminders = [], todos = [],
  currentAgent = '', isSuperadmin = false,
}) {
  const today = todayWIB()
  const [date, setDate] = useState(today)
  const [filterAgent, setFilterAgent] = useState('')
  const [showDone, setShowDone] = useState(true)

  // Agents only ever see their own data; superadmin may filter.
  const scopeAgent = isSuperadmin ? filterAgent : currentAgent

  const items = useMemo(
    () => dayAgenda({ date, tasks, reminders, todos, agent: scopeAgent, includeDone: showDone }),
    [date, tasks, reminders, todos, scopeAgent, showDone]
  )

  // 7-day strip around the selected date with per-day counts.
  const counts = useMemo(
    () => agendaCounts({ tasks, reminders, todos, agent: scopeAgent, includeDone: false }),
    [tasks, reminders, todos, scopeAgent]
  )
  const strip = Array.from({ length: 7 }, (_, i) => addDays(date, i - 3))

  return (
    <div>
      {/* Date nav */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <button onClick={() => setDate(addDays(date, -1))} style={navBtn}><ChevronLeft size={16} /></button>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#111', minWidth: 210, textAlign: 'center' }}>{prettyDate(date)}</div>
        <button onClick={() => setDate(addDays(date, 1))} style={navBtn}><ChevronRight size={16} /></button>
        <button onClick={() => setDate(today)} style={{ ...navBtn, width: 'auto', padding: '0 10px', fontSize: 12, fontWeight: 600 }}>Hari ini</button>
        <input type="date" value={date} onChange={e => setDate(e.target.value)}
          style={{ fontSize: 12, padding: '6px 9px', border: '0.5px solid rgba(0,0,0,0.2)', borderRadius: 8 }} />
        <label style={{ fontSize: 12, color: '#666', display: 'flex', alignItems: 'center', gap: 4, marginLeft: 8 }}>
          <input type="checkbox" checked={showDone} onChange={e => setShowDone(e.target.checked)} /> Tampilkan selesai
        </label>
        {isSuperadmin && (
          <select value={filterAgent} onChange={e => setFilterAgent(e.target.value)}
            style={{ marginLeft: 'auto', fontSize: 12, padding: '6px 10px', border: '0.5px solid rgba(0,0,0,0.2)', borderRadius: 8, fontWeight: 600 }}>
            <option value="">Semua agent</option>
            {TELESALES_AGENTS.map(a => <option key={a}>{a}</option>)}
          </select>
        )}
      </div>

      {/* Week strip */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14, overflowX: 'auto' }}>
        {strip.map(d => {
          const active = d === date
          const isToday = d === today
          const n = counts[d] || 0
          const dd = new Date(d + 'T00:00:00')
          return (
            <button key={d} onClick={() => setDate(d)}
              style={{
                flex: '1 1 0', minWidth: 54, padding: '8px 4px', borderRadius: 10, cursor: 'pointer',
                border: '0.5px solid ' + (active ? '#185FA5' : 'rgba(0,0,0,0.12)'),
                background: active ? '#185FA5' : (isToday ? '#E6F1FB' : '#fff'),
                color: active ? '#fff' : '#333', textAlign: 'center',
              }}>
              <div style={{ fontSize: 10, opacity: 0.8 }}>{dd.toLocaleDateString('id-ID', { weekday: 'short' })}</div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{dd.getDate()}</div>
              <div style={{ height: 14 }}>
                {n > 0 && <span style={{ fontSize: 10, fontWeight: 700, background: active ? 'rgba(255,255,255,0.25)' : '#185FA522', color: active ? '#fff' : '#185FA5', padding: '0 6px', borderRadius: 99 }}>{n}</span>}
              </div>
            </button>
          )
        })}
      </div>

      {/* Agenda */}
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#111' }}>Agenda {prettyDate(date)}</span>
          <Badge variant="info">{items.length} item</Badge>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, fontSize: 11, color: '#888' }}>
            <LegendDot color={KIND_ACCENT.reminder} label="Follow-up" />
            <LegendDot color={KIND_ACCENT.task} label="Task" />
            <LegendDot color={KIND_ACCENT.todo} label="To-Do" />
          </div>
        </div>
        {items.length === 0 ? (
          <EmptyState>Tidak ada agenda untuk tanggal ini.</EmptyState>
        ) : items.map(it => (
          <div key={it.kind + it.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 2px', borderBottom: '0.5px solid rgba(0,0,0,0.07)', opacity: it.done ? 0.55 : 1 }}>
            <div style={{ width: 52, flexShrink: 0, fontSize: 12, fontWeight: 700, color: '#555', display: 'flex', alignItems: 'center', gap: 3 }}>
              {it.time ? (<><Clock size={11} /> {it.time}</>) : <span style={{ color: '#bbb' }}>—</span>}
            </div>
            <div style={{ width: 4, alignSelf: 'stretch', borderRadius: 4, background: KIND_ACCENT[it.kind], flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#111', textDecoration: it.done ? 'line-through' : 'none', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.title}</div>
              {it.sub && <div style={{ fontSize: 12, color: '#666', marginTop: 1 }}>{it.sub}</div>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
              <Badge variant={KIND_VARIANT[it.kind]}>{it.badge}</Badge>
              {isSuperadmin && it.agent && <span style={{ fontSize: 11, color: '#999' }}>{it.agent}</span>}
            </div>
          </div>
        ))}
      </Card>
    </div>
  )
}

function LegendDot({ color, label }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span style={{ width: 8, height: 8, borderRadius: 2, background: color }} /> {label}
    </span>
  )
}

const navBtn = { width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', border: '0.5px solid rgba(0,0,0,0.2)', borderRadius: 8, cursor: 'pointer', color: '#333' }
